import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
}

/**
 * Every page's header: the title (the page's h1) and optional subtitle on the left, the children
 * (actions) on the right; stacked on a narrow screen.
 *
 *   <PageHeader title="Schedules">
 *     <Button …>Add schedule</Button>
 *   </PageHeader>
 */
export const PageHeader = (props: PageHeaderProps) => {
  const { title, subtitle, children } = props;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{
        alignItems: { sm: "center" },
        flexShrink: 0,
        pb: 2.5,
        pt: 2,
        px: 3,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "baseline",
          columnGap: 2,
          flexWrap: "wrap",
          minWidth: 0,
          rowGap: 0.5,
        }}
      >
        <Typography
          component="h1"
          variant="h1"
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            color="text.secondary"
            component="div"
            variant="body2"
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Box sx={{ flex: 1 }} />
      {children && (
        <Stack
          direction="row"
          spacing={2.5}
          sx={{ alignItems: "center" }}
        >
          {children}
        </Stack>
      )}
    </Stack>
  );
};
